/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, List, Drawer, Slide, CircularProgress } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import { Paths, PROCESS_EVENTS, USER_PROCESSES } from '../../../Constants/App';
import { getStoredCustomerDetails, hasEventId } from '../../../Utilities/utils';
import sizeConfigs, { tabletSizeConfigs } from '../../../Configs/sizeConfigs';

import CustomerApiService from '../../../../Modules/Customer/Services/CustomerService';

import AllSchedulesMenu from './AllSchedulesMenu';
import AllJobsMenu from './AllJobsMenu';
import { clearFilters } from '../../../Store/features/filtersSlice';
import { clearSelection } from '../../../Store/features/jobSelectionSlice';

import theme from '../../../Themes/theme';
import { SubMenuDialogWrapper } from '../../../../Modules/Jobs/Components/TaskDetails/TaskDetailsPage.Style';
import { LeftNavBarField, styles } from './LeftNavBar.styles';
import { Loader } from '../../../../Modules/Jobs/Components/JobView/JobsView.styles';

interface ISubMenuDialogProps {
  handleCloseApproveWindow: () => void;
  isApproveWindowOpen: boolean;
  isSideBarVisible?: boolean;
  isTablet?: boolean;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="right" ref={ref} {...props} />;
});

const SubMenuDialog = ({
  isApproveWindowOpen,
  handleCloseApproveWindow,
  isSideBarVisible,
  isTablet,
}: ISubMenuDialogProps) => {
  const sizeConfig = isTablet ? tabletSizeConfigs : sizeConfigs;
  const sidebarWidth = isSideBarVisible ? sizeConfig.sidebar.openWidth : sizeConfig.sidebar.closedWidth;
  const { customerId = '', userId } = getStoredCustomerDetails() || {};

  const [activeMenu, setActiveMenu] = useState<string>('');
  const [processEvents, setProcessEvents] = useState([]);
  const [isProcessEventsLoading, setIsProcessEventsLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleMenuClick = (menu: string) => {
    setActiveMenu(menu);
    dispatch(clearFilters());
    dispatch(clearSelection());
    navigate(menu, { state: { processEvents } });
    handleCloseApproveWindow();
  };

  const fetchProcessEvents = async () => {
    try {
      setIsProcessEventsLoading(true);
      if (userId && customerId) {
        const processEvents = await CustomerApiService.fetchProcessEvents(
          userId,
          customerId,
          Number(USER_PROCESSES.JobManagementDashboard)
        );
        localStorage.setItem('jobManagementProcessEvents', JSON.stringify(processEvents?.data || []));
        setProcessEvents(processEvents?.data);
      }
      setIsProcessEventsLoading(false);
    } catch (error) {
      console.error('Error fetching process events:', error);
    } finally {
      setIsProcessEventsLoading(false);
    }
  };

  const jobManagementEvents = localStorage.getItem('jobManagementProcessEvents');

  useEffect(() => {
    if (isApproveWindowOpen) {
      if (jobManagementEvents) {
        setProcessEvents(JSON.parse(jobManagementEvents));
        return;
      }
      fetchProcessEvents();
    }
  }, [isApproveWindowOpen]);

  return (
    <SubMenuDialogWrapper
      sx={{ left: sidebarWidth }}
      open={isApproveWindowOpen}
      onClose={() => {
        handleCloseApproveWindow();
      }}
      TransitionComponent={Transition}
      transitionDuration={250}>
      <Drawer variant="persistent" anchor="left" open={true}>
        <LeftNavBarField>
          <List
            sx={{
              alignItems: 'flex-start',
              ...styles.leftNavBarIcons,
            }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '4rem',
              }}>
              {isProcessEventsLoading && (
                <Loader>
                  <CircularProgress style={{ marginTop: '2rem', color: theme.palette.primary.dark }} />
                </Loader>
              )}
              {!isProcessEventsLoading && (
                <>
                  {hasEventId(processEvents, PROCESS_EVENTS.ChangeSubVendor) && (
                    <AllJobsMenu
                      theme={theme}
                      handleMenuClick={() => handleMenuClick(Paths.AllJobs)}
                      isActive={activeMenu === Paths.AllJobs}
                    />
                  )}
                  {hasEventId(processEvents, PROCESS_EVENTS.ManageJobDetails) && (
                    <AllSchedulesMenu
                      theme={theme}
                      handleMenuClick={() => handleMenuClick(Paths.AllSchedules)}
                      isActive={activeMenu === Paths.AllSchedules}
                    />
                  )}
                </>
              )}
            </Box>
          </List>
        </LeftNavBarField>
      </Drawer>
    </SubMenuDialogWrapper>
  );
};

SubMenuDialog.defaultProps = {
  rowDetails: '',
  isShowApproveAll: false,
  isShowCloseIcon: true,
};

export default withTranslation(['dashboard', 'logout', 'altTexts'])(SubMenuDialog);
